import React, { useRef, useEffect, useState } from "react";
import { ChatMessage } from "lib/models";
import { FaChevronDown, FaArchive } from "react-icons/fa";
import { createNewChat } from "lib/api";
import Button, { ButtonVariant } from "components/Button";
import { useAppContext } from "lib/state/TextProvider";
import ChatMsgContainer from "./ChatMsgContainer";

interface ChatContainerProps {
  messages: ChatMessage[];
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  hideCollapseButton?: boolean;
}

function ChatContainer({
  messages,
  isCollapsed,
  setIsCollapsed,
  hideCollapseButton,
}: ChatContainerProps) {
  const { state, setUserProfileNeedsReload } = useAppContext();
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState<boolean>(true);

  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isCollapsed && chatContainerRef.current) {
      setIsAutoScrollEnabled(true);
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (isAutoScrollEnabled && chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, isAutoScrollEnabled]);

  const archiveThreadAndCreateNewChat = async () => {
    if (!state.userProfile) {
      return;
    }
    await createNewChat();
    setUserProfileNeedsReload(true);
  };

  const handleOnScroll = (event: any): void => {
    // Enable auto-scroll if user has scrolled to bottom of chat messages
    const target = event.currentTarget;
    const atBottom: boolean =
      Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
      1;
    setIsAutoScrollEnabled(atBottom);
  };

  if (isCollapsed) {
    return null;
  }

  return (
    <div className="w-full h-full">
      <div
        ref={chatContainerRef}
        onScroll={handleOnScroll}
        className="p-2 space-y-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-350px)]"
      >
        {messages.map((msg, idx) => {
          return (
            <div key={`chatMsg:${idx}`} className="w-full">
              <ChatMsgContainer msg={msg} />
            </div>
          );
        })}
      </div>
      <div className="flex flex-row justify-end py-4 space-x-4">
        <Button
          variant={ButtonVariant.FlatNoFill}
          icon={<FaArchive />}
          onClick={() => archiveThreadAndCreateNewChat()}
          disabled={!messages.length}
          size="sm"
        />
        {hideCollapseButton ? null : (
          <Button
            variant={ButtonVariant.FlatNoFill}
            icon={<FaChevronDown />}
            onClick={() => setIsCollapsed(true)}
            size="sm"
          />
        )}
      </div>
      <hr className="border-t-2 border-palette-4" />
    </div>
  );
}

export default ChatContainer;
